/**
 * Modal store — Zustand slice for the global modal queue.
 *
 * Phase 1 (post-walkthrough):
 * - Added `confirmCopy?: string` to ModalRequest props — gates the
 *   Confirm button behind a type-to-match input for high-blast-radius
 *   destructive actions (Delete Tenant, Delete JD with active candidates).
 *
 * Spec: design/post-walkthrough/global-patterns-spec.md §1.4.
 */
import { create } from "zustand";

export type ModalType = "terms-reaccept" | "privacy-reaccept" | "confirm";
export type ModalResult = "confirmed" | "dismissed" | "declined";

type ModalRequest = {
  type: ModalType;
  props: Record<string, unknown> & {
    /**
     * When present, the Confirm/destructive action is gated behind a
     * type-to-match text input. The user must type this exact string before
     * the confirm button enables. Per spec §1.4 "high-blast-radius destructive."
     */
    confirmCopy?: string;
  };
};

type ModalState = {
  current: (ModalRequest & { resolve: (result: ModalResult) => void }) | null;
  open: (request: ModalRequest) => Promise<ModalResult>;
  resolve: (result: ModalResult) => void;
};

export const useModalStore = create<ModalState>((set, get) => ({
  current: null,

  open: (request) => {
    return new Promise<ModalResult>((resolve) => {
      set({ current: { ...request, resolve } });
    });
  },

  resolve: (result) => {
    const { current } = get();
    if (current) {
      current.resolve(result);
      set({ current: null });
    }
  },
}));
